// src/components/TopBar/TopBar.jsx
// Mudo Ceramics Top Bar — drop-in
//
// Single-row layout:
// - Left: logo + product name + hamburger
// - Middle: (md+) breadcrumb/title
// - Right (all inline): sound, theme, SyncPill, search, Canva, Help, bell,
//   plan pill, days-left pill, profile
//
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Menu,
  CreditCard,
  Bell,
  Mail,
  Sun,
  Moon,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useVault } from "../../contexts/VaultContext";
import { useHelp } from "../../contexts/HelpContext";
import HelpCenterModal from "../../worlds/ceramics/components/help/HelpCenterModal.jsx";
import { globalSearch } from "../../utils/globalSearch";
import { navigateTo, ROUTES } from "../../lib/nav";
import logo from "../../assets/mudo-logo-bg.png";
import SyncPill from "../Sync/SyncPill";
import { useNotificationsForWorld } from "../../contexts/NotificationsContext";
import ContactUsModal from "../Modals/ContactUsModal";
import { SmallText } from "../../ui/Typography";
import Button from "../../ui/Button";
import ProfileDropdown from "../../ui/ProfileDropdown";
import { useAuth } from "../../contexts/AuthContext";
import { apiFetch } from "../../lib/apiFetch";
import { useTheme } from "../../core/theme/ThemeProvider";
import { useAccess } from "../../contexts/AccessContext";

// ---- helpers ---------------------------------------------------
function useLatest(value) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

function useStoredToggle(key, defaultValue) {
  const [value, setValue] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw === "true") return true;
      if (raw === "false") return false;
      return defaultValue;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, String(value));
    } catch {
      // ignore
    }
  }, [key, value]);

  return [value, setValue];
}

// ---- small toggle components -----------------------------------
function SoundToggle({ value, onChange }) {
  const pill =
    "inline-flex items-center gap-1 rounded-full border-[var(--topbar-pill-border)] bg-[var(--topbar-pill-bg)] text-[var(--topbar-pill-text)] hover:bg-[var(--topbar-pill-bg-hover)] px-2 py-1 text-[11px] shrink-0 transition";
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={pill}
      title={value ? "Sound on" : "Sound off"}
    >
      {value ? <Volume2 size={14} /> : <VolumeX size={14} />}
      <span className="hidden sm:inline">Sound</span>
    </button>
  );
}

function ThemeToggle({ dark, onChange }) {
  const pill =
    "inline-flex items-center gap-1 rounded-full border-[var(--topbar-pill-border)] bg-[var(--topbar-pill-bg)] text-[var(--topbar-pill-text)] hover:bg-[var(--topbar-pill-bg-hover)] px-2 py-1 text-[11px] shrink-0 transition";
  return (
    <button
      type="button"
      onClick={() => onChange(!dark)}
      className={pill}
      title={dark ? "Switch to light mode" : "Switch to dark mode"}
    >
      {dark ? <Moon size={14} /> : <Sun size={14} />}
      <span className="hidden sm:inline">{dark ? "Dark" : "Light"}</span>
    </button>
  );
}

// ---- main TopBar ------------------------------------------------
export default function TopBar({
  onCustomizeStudio,
  onChoosePlan,
  onLogout,
  hideShop = false,
  userName,
  avatarUrl,
  onOpenCanva,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { ready, list, getJSON } = useVault();
  const { promo, profile, user: authUser } = useAuth() || {};
  const { accessLabel, daysRemaining, isUnlocked } = useAccess() || {};

  const { toggleHelp } = useHelp();
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);

  const { unreadCount, markAllRead } = useNotificationsForWorld("studio");

  // theme + sound toggles
  const { mode, setMode } = useTheme();
  const darkMode = mode === "dark";
  const [soundOn, setSoundOn] = useStoredToggle("mudo:soundOn", true);

  const textPrimary = "text-[var(--topbar-text)]";
  const textSecondary = "text-[var(--topbar-text-muted)]";
  const borderColor = "border-[var(--topbar-border)]";
  const pillBase =
    "rounded-full border-[var(--topbar-pill-border)] bg-[var(--topbar-pill-bg)] text-[var(--topbar-pill-text)] hover:bg-[var(--topbar-pill-bg-hover)]";

  useEffect(() => {
    window.__mudoSoundEnabled = soundOn;
  }, [soundOn]);

  // Local ticker: force re-render every 30s so countdown updates
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const daysLabel = useMemo(() => {
    // Use accessLabel from AccessContext (Lifetime, 30 days, Expired, etc.)
    if (import.meta.env.DEV) {
      console.log("[TopBar] Days label updated:", {
        accessLabel,
        daysRemaining,
        isUnlocked,
      });
    }
    return accessLabel || "Loading...";
  }, [accessLabel, daysRemaining, isUnlocked]);

  const effectiveUserName =
    userName ||
    profile?.display_name ||
    profile?.full_name ||
    authUser?.email?.split("@")[0] ||
    "Guest";

  const effectiveAvatarUrl = avatarUrl || profile?.avatar_url || null;

  // ---------- Quick Search ----------
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef(null);

  const listRef = useLatest(list);
  const getJSONRef = useLatest(getJSON);

  useEffect(() => {
    if (!ready) {
      setResults([]);
      return;
    }
    if (!search || !search.trim()) {
      setResults([]);
      setShowResults(false);
      return;
    }

    let alive = true;
    const ctrl = new AbortController();

    const t = setTimeout(async () => {
      try {
        const matches = await globalSearch(search, {
          list: listRef.current,
          getJSON: getJSONRef.current,
          signal: ctrl.signal,
        });
        if (!alive) return;
        setResults(Array.isArray(matches) ? matches : []);
        setShowResults(true);
        setSelectedIndex(0);
      } catch (err) {
        if (err?.name !== "AbortError") {
          // swallow
        }
      }
    }, 100);

    return () => {
      alive = false;
      ctrl.abort();
      clearTimeout(t);
    };
  }, [search, ready, listRef, getJSONRef]);

  const clearSearch = () => {
    setSearch("");
    setResults([]);
    setShowResults(false);
    setSelectedIndex(0);
  };

  const navigateToResult = (index) => {
    if (!results[index]) return;
    const result = results[index];
    const target = result.path || inferRoute(result.section, result.label, search);
    navigateTo(navigate, target, { source: "TopBar:search" });
    clearSearch();
  };

  const handleKeyDown = (e) => {
    if (!showResults || results.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % results.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + results.length) % results.length);
        break;
      case "Enter":
        e.preventDefault();
        navigateToResult(selectedIndex);
        break;
      case "Escape":
        e.preventDefault();
        clearSearch();
        searchInputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  function inferRoute(section = "", label = "", query = "") {
    const text = `${String(section)} ${String(label)} ${String(query)}`.toLowerCase();

    if (/\bshop\b/.test(text)) return ROUTES.SHOP;
    if (/\bstaff\b|mudostaff|mudo_staff/.test(text)) return ROUTES.STAFF;
    if (/\b(planner|task|gantt)\b|mudo ?tasks?/.test(text))
      return ROUTES.PLANNER_TASKS;
    if (/\b(product|gallery)\b/.test(text)) return ROUTES.GALLERY;
    if (/\bmaterial(s)?\b|mudo ?materials?/.test(text))
      return ROUTES.MATERIALS;
    if (/\b(po|purchase|purchase-?orders?)\b/.test(text))
      return ROUTES.PURCHASE_ORDERS;
    if (/\b(batch|kiln)\b/.test(text)) return ROUTES.BATCHES;
    if (/\bworkshop(s)?\b/.test(text)) return ROUTES.WORKSHOPS;
    if (/\b(piece|my-?pieces)\b/.test(text)) return ROUTES.MY_PIECES;
    if (/\breceipt(s)?\b/.test(text)) return ROUTES.RECEIPTS;
    if (/\bpackaging\b/.test(text)) return ROUTES.PACKAGING_LIBRARY;
    if (/\bsetting(s)?\b|data\s*&\s*backup|backup/.test(text))
      return ROUTES.SETTINGS;
    return ROUTES.DASHBOARD;
  }

  function highlightSnippet(snippet, q) {
    if (!snippet || !q) return snippet || "";
    const s = snippet.toLowerCase();
    const t = q.toLowerCase();
    const i = s.indexOf(t);
    if (i === -1) return snippet;
    const before = snippet.slice(0, i);
    const match = snippet.slice(i, i + q.length);
    const after = snippet.slice(i + q.length);
    return (
      <>
        {before}
        <mark className="bg-transparent text-[var(--mudo-text)] font-bold">
          {match}
        </mark>
        {after}
      </>
    );
  }

  const { pageTitle, breadcrumb } = useMemo(() => {
    const p = location.pathname || "/";
    const map = {
      "/": { title: "Welcome", crumb: "" },
      "/home": { title: "Home", crumb: "" },
      "/dashboard": { title: "Dashboard", crumb: "Home › Dashboard" },
      "/dashboard/home": { title: "Dashboard", crumb: "Home › Dashboard" },
      "/dashboard/gallery": { title: "Gallery", crumb: "Home › Gallery" },
      "/dashboard/planner": { title: "Planner", crumb: "Home › Planner" },
      "/dashboard/planner/tasks": {
        title: "Planner",
        crumb: "Home › Planner › Tasks",
      },
      "/dashboard/planner/gantt": {
        title: "Planner",
        crumb: "Home › Planner › Gantt",
      },
      "/dashboard/batches": { title: "Batches", crumb: "Home › Batches" },
      "/dashboard/materials": { title: "Materials", crumb: "Home › Materials" },
      "/dashboard/purchase-orders": {
        title: "Purchase Orders",
        crumb: "Home › POs",
      },
      "/dashboard/receipts": { title: "Receipts", crumb: "Home › Receipts" },
      "/dashboard/workshops": {
        title: "Workshops",
        crumb: "Home › Workshops",
      },
      "/dashboard/my-pieces": {
        title: "Track My Pieces",
        crumb: "Home › My Pieces",
      },
      "/dashboard/shop": { title: "Shop", crumb: "Home › Shop" },
      "/dashboard/forum": { title: "Forum", crumb: "Home › Forum" },
      "/dashboard/help": { title: "Help", crumb: "Home › Help" },
      "/dashboard/notifications": { title: "Notifications", crumb: "Home › Notifications" },
      "/dashboard/settings": { title: "Settings", crumb: "Home › Settings" },
      "/dashboard/analytics": {
        title: "Analytics",
        crumb: "Home › Analytics",
      },
      "/dashboard/time-logs": {
        title: "Time Logs",
        crumb: "Home › Time Logs",
      },
      "/dashboard/staff": { title: "Staff", crumb: "Home › Staff" },
      "/dashboard/packaging/library": {
        title: "Packaging Library",
        crumb: "Home › Packaging › Library",
      },
      "/dashboard/packaging/rules": {
        title: "Packaging Rules",
        crumb: "Home › Packaging › Rules",
      },
      "/dashboard/kilns": { title: "Kilns", crumb: "Home › Kilns" },
      "/dashboard/kiln-planner": {
        title: "Kiln Planner",
        crumb: "Home › Kiln Planner",
      },
      "/dashboard/settings/data": {
        title: "Data & Backup",
        crumb: "Home › Settings › Data & Backup",
      },
    };
    return map[p] || { title: "Mudo", crumb: "" };
  }, [location.pathname]);

  // ----- Drawer -----
  const [open, setOpen] = useState(false);
  const drawerRef = useRef(null);

  const openDrawer = () => {
    setShowResults(false);
    setSearch("");
    setOpen(true);
  };
  const closeDrawer = () => setOpen(false);

  const go = (to) => {
    closeDrawer();
    navigateTo(navigate, to, { source: "TopBar:drawer" });
  };

  useEffect(() => {
    const onDoc = (e) => {
      if (open && drawerRef.current && !drawerRef.current.contains(e.target)) {
        closeDrawer();
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (open && e.key === "Escape") closeDrawer();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  async function openBillingPortal() {
    try {
      const email = localStorage.getItem("mudo:userEmail");
      if (!email) {
        alert("Please log in first so we can locate your billing account.");
        return;
      }
      const r = await apiFetch(`/api/billing/portal?email=${encodeURIComponent(email)}`);
      const json = await r.json();
      if (json?.url) window.location.href = json.url;
      else alert(json?.error || "Could not open billing portal.");
    } catch (e) {
      alert(e.message || "Billing portal failed.");
    }
  }

  return (
    <>
      <header
        className={`sticky top-0 z-40 w-full border-b shadow-[var(--mudo-shadow-subtle)] ${borderColor} bg-[var(--topbar-bg)] text-[var(--topbar-text)]`}
        style={{
          backdropFilter: "blur(var(--topbar-bg-blur))",
          WebkitBackdropFilter: "blur(var(--topbar-bg-blur))",
        }}
      >
        <div className="w-full px-3 sm:px-6">
          <div className="flex items-center gap-2 sm:gap-3 py-2 flex-nowrap">
            {/* LEFT: logo + product + hamburger */}
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <button
                type="button"
                className="flex items-center gap-2 rounded-full px-2 py-1 transition focus:outline-none focus:ring-2 focus:ring-[var(--topbar-border)] hover:bg-[var(--topbar-pill-bg-hover)]"
                onClick={() =>
                  navigateTo(navigate, ROUTES.DASHBOARD, { source: "TopBar:logo" })
                }
                title="Go to Dashboard"
              >
                <img 
                  src={logo} 
                  alt="Mudo" 
                  className="h-10 w-10 object-contain"
                  loading="eager"
                  decoding="async"
                  width={40}
                  height={40}
                />
                <div className="leading-tight text-left">
                  <div className={`text-sm font-semibold ${textPrimary}`}>
                    Mudo
                  </div>
                  <SmallText className={textSecondary}>ceramics</SmallText>
                </div>
              </button>

              <Button
                variant="ghost"
                aria-label="Open menu"
                onClick={openDrawer}
                title="Menu"
                className={`h-10 w-10 rounded-full p-0 shrink-0 ${textPrimary} ${pillBase}`}
              >
                <Menu size={22} className="shrink-0" />
              </Button>
            </div>

            {/* MIDDLE: breadcrumb (optional, md+) */}
            <div className="hidden md:flex flex-col items-start justify-center mx-3 min-w-0">
              {breadcrumb ? (
                <SmallText className={`${textSecondary} truncate`}>
                  {breadcrumb}
                </SmallText>
              ) : null}
              <div className={`text-sm font-semibold leading-tight ${textPrimary} truncate`}>
                {pageTitle}
              </div>
            </div>

            {/* RIGHT: everything inline, hugging the right edge */}
            <div className="ml-auto flex items-center gap-2 sm:gap-3 flex-nowrap overflow-visible">
              {/* Sound + theme */}
              <SoundToggle value={soundOn} onChange={setSoundOn} />
              <ThemeToggle
                dark={darkMode}
                onChange={(next) => setMode(next ? "dark" : "light")}
              />

              {/* Send / Receive pill */}
              <div className="shrink-0">
                <SyncPill className={textPrimary} dark={darkMode} />
              </div>

              {/* Promo badge (marketing-ish) */}
              {promo && (promo.isLifetime || promo.isActive) && (
                <span
                  className={`rounded-full px-2.5 py-0.5 text-[11px] border shrink-0 whitespace-nowrap ${
                    darkMode
                      ? "border-[var(--mudo-primary)] bg-[var(--mudo-primary-soft)] text-[var(--mudo-primary)]"
                      : "border-amber-200 bg-amber-50 text-amber-700"
                  }`}
                >
                  {promo.isLifetime
                    ? `${promo.code || "BLS-Free"} · Lifetime access`
                    : `${promo.discountPercent === 100 ? "Free" : `${promo.discountPercent}% off`} · ${Math.max(
                        0,
                        promo.daysRemaining ?? 0
                      )} days left`}
                </span>
              )}

              {/* Search */}
              <div className="relative min-w-[160px] max-w-[260px] sm:max-w-[320px] shrink-0">
                <div className="flex items-center gap-2 rounded-full border border-[var(--topbar-border)] bg-[var(--topbar-pill-bg)] px-3 py-2 text-[var(--topbar-text)] shadow-sm">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                    className="text-[var(--topbar-text-muted)]"
                  >
                    <path
                      d="M21 21l-4.3-4.3"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    />
                    <circle
                      cx="11"
                      cy="11"
                      r="7"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    />
                  </svg>
                  <input
                    ref={searchInputRef}
                    className="flex-1 bg-transparent text-sm text-[var(--topbar-text)] placeholder:text-[var(--topbar-text-muted)] focus:outline-none"
                    placeholder="Quick search (Ctrl+K)"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onFocus={() => {
                      if (search.trim() && results.length > 0) {
                        setShowResults(true);
                      }
                    }}
                    onKeyDown={handleKeyDown}
                    onBlur={() => setTimeout(() => setShowResults(false), 200)}
                    aria-label="Quick search"
                    aria-autocomplete="list"
                    aria-controls="search-results"
                    aria-expanded={showResults}
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearSearch();
                      }}
                      className="text-xs transition text-[var(--topbar-text-muted)] hover:text-[var(--topbar-text)]"
                      title="Clear search"
                    >
                      ✕
                    </button>
                  )}
                </div>

                {showResults && results.length > 0 && (
                  <div
                    id="search-results"
                    role="listbox"
                    className="absolute right-0 top-[110%] z-50 max-h-[460px] w-[280px] sm:w-[340px] overflow-y-auto rounded-xl border border-[var(--topbar-border)] bg-[var(--topbar-pill-bg)]/95 p-2 text-[var(--topbar-text)] shadow-2xl backdrop-blur-md"
                  >
                    {results.map((r, i) => {
                      const target = r.path || inferRoute(r.section, r.label, search);
                      const isSelected = i === selectedIndex;
                      return (
                        <div
                          key={i}
                          role="option"
                          aria-selected={isSelected}
                          tabIndex={-1}
                          className={`cursor-pointer rounded-lg px-3 py-2 text-sm transition ${
                            isSelected
                              ? "bg-[var(--topbar-pill-bg-hover)] text-[var(--topbar-text)]"
                              : "text-[var(--topbar-text)] hover:bg-[var(--topbar-pill-bg-hover)]"
                          }`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            navigateTo(navigate, target, {
                              source: "TopBar:search-result",
                            });
                            clearSearch();
                          }}
                          onMouseEnter={() => setSelectedIndex(i)}
                        >
                          <div className="flex items-center gap-2">
                            <div className="flex-1 truncate font-semibold">{r.label}</div>
                            <div className="whitespace-nowrap text-[10px] uppercase tracking-[0.5px] text-[var(--topbar-text-muted)]">
                              {r.section}
                            </div>
                          </div>

                          {r.snippet && (
                            <div
                              className="mt-1 text-[11px] leading-snug text-[var(--topbar-text-muted)]"
                              title={r.snippet}
                            >
                              {highlightSnippet(r.snippet, search)}
                            </div>
                          )}

                          <div className="mt-2 font-mono text-[10px] text-[var(--topbar-text-muted)]">
                            {target}
                          </div>
                        </div>
                      );
                    })}

                    <div className="mt-1 border-t border-[var(--topbar-border)] pt-2 text-center text-[10px] text-[var(--topbar-text-muted)]">
                      ↑↓ Navigate • Enter to select • Esc to close
                    </div>
                  </div>
                )}

                {showResults && results.length === 0 && search.trim() && (
                  <div className="absolute right-0 top-[110%] z-50 w-[260px] sm:w-[320px] rounded-xl border border-[var(--topbar-border)] bg-[var(--topbar-pill-bg)]/95 p-4 text-center text-[var(--topbar-text)] shadow-2xl backdrop-blur-md">
                    <div className="text-sm text-[var(--topbar-text)]">
                      No results found for <strong>{search}</strong>
                    </div>
                    <div className="mt-2 text-[11px] text-[var(--topbar-text-muted)]">
                      Try searching for materials, products, tasks, staff, or pages
                    </div>
                  </div>
                )}
              </div>

              {/* Canva */}
              {onOpenCanva && (
                <Button
                  variant="soft"
                  size="sm"
                  onClick={onOpenCanva}
                  title="Open Canva Studio"
                  className={`h-9 px-3 py-1.5 text-sm rounded-full shrink-0 ${pillBase}`}
                >
                  <span className="inline-flex items-center gap-2">
                    <img 
                      src="/canva-icon.svg" 
                      alt="Canva" 
                      className="h-5 w-5"
                      loading="lazy"
                      decoding="async"
                      width={20}
                      height={20}
                    />
                    <span>Canva</span>
                  </span>
                </Button>
              )}

              {/* Ask AI */}
              <Button
                variant="soft"
                size="sm"
                onClick={toggleHelp}
                title="Ask Mudo Assistant"
                className={`h-9 px-3 py-1.5 text-sm rounded-full shrink-0 ${pillBase}`}
              >
                Ask AI
              </Button>

              {/* Help Center */}
              <Button
                variant="soft"
                size="sm"
                onClick={() => setIsHelpOpen(true)}
                title="Help Center"
                className={`h-9 px-3 py-1.5 text-sm rounded-full shrink-0 ${pillBase}`}
              >
                Help Center
              </Button>

              {/* Bell */}
              <Button
                variant="ghost"
                className={`relative h-10 w-10 rounded-full p-0 shrink-0 ${pillBase}`}
                onClick={() => {
                  markAllRead?.();
                  navigateTo(navigate, ROUTES.NOTIFICATIONS, { source: "TopBar:notifications" });
                }}
                aria-label="Open inbox"
              >
                <Bell size={22} className="shrink-0" />
                {unreadCount > 0 && (
                  <span className="absolute -right-1 -top-1 inline-flex min-h-[16px] min-w-[16px] items-center justify-center rounded-full border border-black/30 bg-[var(--mudo-primary)] px-1 text-[10px] font-bold text-[var(--mudo-onPrimary)]">
                    {(() => {
                      const total = unreadCount || 0;
                      return total > 9 ? "9+" : total;
                    })()}
                  </span>
                )}
              </Button>

              {/* Plan pill (clickable to open subscription modal) */}
              <button
                onClick={() => onChoosePlan?.()}
                className={`rounded-full px-3 py-1 text-xs font-medium shrink-0 cursor-pointer transition ${pillBase} ${textSecondary} hover:opacity-80`}
                title={isUnlocked ? "Manage your subscription plan" : "Unlock premium features"}
              >
                {isUnlocked ? "Manage Plan" : "Unlock"}
              </button>

              {/* Days-left pill (always show) */}
              <span
                className={`rounded-full px-2.5 py-0.5 text-[11px] border shrink-0 ${
                  isUnlocked === null || isUnlocked === undefined
                    ? "border-gray-300/40 bg-gray-500/10 text-gray-400"
                    : !isUnlocked
                    ? "border-red-300/40 bg-red-500/10 text-red-200"
                    : darkMode
                    ? "border-[var(--mudo-primary)] bg-[var(--mudo-primary-soft)] text-[var(--mudo-primary)]"
                    : "border-emerald-200 bg-emerald-50 text-emerald-700"
                }`}
                title="Access time remaining"
              >
                {daysLabel}
              </span>

              <ProfileDropdown
                user={{ name: effectiveUserName, avatarUrl: effectiveAvatarUrl }}
                onSignOut={onLogout}
                onCustomizeStudio={onCustomizeStudio}
                onChoosePlan={onChoosePlan}
              />
            </div>
          </div>

          {/* Help Center Modal */}
          <HelpCenterModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
        </div>
      </header>

      {/* Drawer backdrop */}
      {open && <div className="fixed inset-0 z-40 bg-black/45" onClick={closeDrawer} />}

      {/* Drawer */}
      <aside
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Main menu"
        className={`fixed left-0 top-0 z-50 flex h-screen w-72 transform flex-col border-r border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)]/95 text-[var(--mudo-text)] shadow-2xl backdrop-blur-lg transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-[64px] items-center justify-between border-b border-[var(--mudo-border)] px-4">
          <div className="flex items-center gap-2">
            <img 
              src={logo} 
              alt="Mudo" 
              className="h-7 w-7"
              loading="eager"
              decoding="async"
              width={28}
              height={28}
            />
            <div className="leading-tight">
              <div className="text-sm font-semibold text-[var(--mudo-text)]">Mudo</div>
              <SmallText className="text-[var(--mudo-text-muted)]">ceramics</SmallText>
            </div>
          </div>
          <Button
            variant="ghost"
            type="button"
            aria-label="Close menu"
            onClick={closeDrawer}
            title="Close"
            className="h-9 px-3"
          >
            Close
          </Button>
        </div>

        {/* NAVIGATION - Always visible */}
        <div className="px-4 pt-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--mudo-text-muted)]">
          Navigation
        </div>
        <nav className="flex flex-col gap-2 p-3">
          <button
            className="flex items-center gap-2 rounded-xl border border-transparent bg-[color-mix(in_lab,var(--mudo-bg-soft),transparent_20%)] px-3 py-3 text-sm font-semibold text-[var(--mudo-text)] transition hover:border-[var(--mudo-border)]"
            onClick={() => go(ROUTES.DASHBOARD_HOME)}
          >
            Home
          </button>
          <button
            className="flex items-center gap-2 rounded-xl border border-transparent bg-[color-mix(in_lab,var(--mudo-bg-soft),transparent_20%)] px-3 py-3 text-sm font-semibold text-[var(--mudo-text)] transition hover:border-[var(--mudo-border)]"
            onClick={() => go(ROUTES.DASHBOARD)}
          >
            Dashboard
          </button>
          <button
            className="flex items-center gap-2 rounded-xl border border-transparent bg-[color-mix(in_lab,var(--mudo-bg-soft),transparent_20%)] px-3 py-3 text-sm font-semibold text-[var(--mudo-text)] transition hover:border-[var(--mudo-border)]"
            onClick={() => go(ROUTES.PLANNER_TASKS)}
          >
            Planner
          </button>
          <button
            className="flex items-center gap-2 rounded-xl border border-transparent bg-[color-mix(in_lab,var(--mudo-bg-soft),transparent_20%)] px-3 py-3 text-sm font-semibold text-[var(--mudo-text)] transition hover:border-[var(--mudo-border)]"
            onClick={() => go(ROUTES.KILN_PLANNER)}
          >
            Kiln Planner
          </button>
          <button
            className="flex items-center gap-2 rounded-xl border border-transparent bg-[color-mix(in_lab,var(--mudo-bg-soft),transparent_20%)] px-3 py-3 text-sm font-semibold text-[var(--mudo-text)] transition hover:border-[var(--mudo-border)]"
            onClick={() => go(ROUTES.KILNS)}
          >
            Kilns
          </button>
          {!hideShop && (
            <button
              className="flex items-center gap-2 rounded-xl border border-transparent bg-[color-mix(in_lab,var(--mudo-bg-soft),transparent_20%)] px-3 py-3 text-sm font-semibold text-[var(--mudo-text)] transition hover:border-[var(--mudo-border)]"
              onClick={() => go(ROUTES.SHOP)}
            >
              Shop
            </button>
          )}
          <button
            className="flex items-center gap-2 rounded-xl border border-transparent bg-[color-mix(in_lab,var(--mudo-bg-soft),transparent_20%)] px-3 py-3 text-sm font-semibold text-[var(--mudo-text)] transition hover:border-[var(--mudo-border)]"
            onClick={() => go(ROUTES.GALLERY)}
          >
            Gallery
          </button>
          <button
            className="flex items-center gap-2 rounded-xl border border-transparent bg-[color-mix(in_lab,var(--mudo-bg-soft),transparent_20%)] px-3 py-3 text-sm font-semibold text-[var(--mudo-text)] transition hover:border-[var(--mudo-border)]"
            onClick={() => go(ROUTES.FORUM)}
          >
            Forum
          </button>
        </nav>

        {/* STUDIO - Always show all options */}
        <div className="px-4 pt-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--mudo-text-muted)]">
          Studio
        </div>
        <div className="flex flex-col gap-2 p-3">
          <button
            className="flex items-center gap-2 rounded-xl border border-transparent bg-[color-mix(in_lab,var(--mudo-bg-soft),transparent_20%)] px-3 py-3 text-sm font-semibold text-[var(--mudo-text)] transition hover:border-[var(--mudo-border)]"
            onClick={() => {
              closeDrawer();
              onCustomizeStudio?.();
            }}
          >
            Customize Studio
          </button>
          <button
            className="flex items-center gap-2 rounded-xl border border-transparent bg-[color-mix(in_lab,var(--mudo-bg-soft),transparent_20%)] px-3 py-3 text-sm font-semibold text-[var(--mudo-text)] transition hover:border-[var(--mudo-border)]"
            onClick={() => {
              closeDrawer();
              navigateTo(navigate, ROUTES.SETTINGS_DATA, {
                source: "TopBar:drawer-settings",
              });
            }}
          >
            Data &amp; Backup
          </button>
          <button
            className="flex items-center gap-2 rounded-xl border border-transparent bg-[color-mix(in_lab,var(--mudo-bg-soft),transparent_20%)] px-3 py-3 text-sm font-semibold text-[var(--mudo-text)] transition hover:border-[var(--mudo-border)]"
            onClick={() => {
              closeDrawer();
              onChoosePlan?.();
            }}
          >
            Choose Plan
          </button>
          <button
            className="flex items-center gap-2 rounded-xl border border-transparent bg-[color-mix(in_lab,var(--mudo-bg-soft),transparent_20%)] px-3 py-3 text-sm font-semibold text-[var(--mudo-text)] transition hover:border-[var(--mudo-border)]"
            onClick={() => {
              closeDrawer();
              openBillingPortal();
            }}
          >
            <CreditCard size={18} />
            <span>Manage Billing</span>
          </button>
          <button
            className="flex items-center gap-2 rounded-xl border border-transparent bg-[color-mix(in_lab,var(--mudo-bg-soft),transparent_20%)] px-3 py-3 text-sm font-semibold text-red-300 transition hover:border-[var(--mudo-border)]"
            onClick={() => {
              closeDrawer();
              onLogout?.();
            }}
          >
            Log Out
          </button>
        </div>

        {/* LEGAL */}
        <div className="px-4 pt-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--mudo-text-muted)]">
          Legal
        </div>
        <div className="flex flex-col gap-2 p-3">
          <button
            className="flex items-center gap-2 rounded-xl border border-transparent bg-[color-mix(in_lab,var(--mudo-bg-soft),transparent_20%)] px-3 py-3 text-sm font-semibold text-[var(--mudo-text)] transition hover:border-[var(--mudo-border)]"
            onClick={() => {
              closeDrawer();
              setIsContactOpen(true);
            }}
          >
            <Mail size={18} />
            <span>Contact us</span>
          </button>
        </div>
      </aside>

      {isContactOpen && (
        <ContactUsModal open={isContactOpen} onClose={() => setIsContactOpen(false)} />
      )}
    </>
  );
}
